import React, { useEffect, useRef, useState } from "react";

/* A looping, muted product video that costs nothing until it is worth
   watching. It renders its poster image first and only attaches the video
   source while it is on screen and drawn at least `minWidth` CSS pixels
   wide (the orbit scales its items with transforms, so the drawn width is
   read from getBoundingClientRect). Off screen it pauses again. With
   reduced motion it stays on the poster. */
export default function LazyVideo({ src, poster, className, minWidth = 220, reducedMotion = false }) {
  const ref = useRef(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video || reducedMotion) return undefined;
    let visible = false;
    let timer = 0;
    const check = () => {
      const rect = video.getBoundingClientRect();
      const big = rect.width >= minWidth && rect.bottom > 0 && rect.top < window.innerHeight;
      if (big) {
        setLoad(true);
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      window.clearInterval(timer);
      if (visible) {
        check();
        timer = window.setInterval(check, 400);
      } else if (!video.paused) {
        video.pause();
      }
    });
    observer.observe(video);
    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [minWidth, reducedMotion]);

  return (
    <video
      ref={ref}
      className={className}
      src={load ? src : undefined}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
    />
  );
}
